import { markChaseItemClosed, unmarkChaseItemClosed, type OpenAsksRead } from './open-asks';
import { CHASE_GROUP_TITLE_KEY, isChaseAsk, withChaseAskRestored, withoutChaseItem, type ChaseAsk } from './chase-asks';

// The pure half of the chaser's ITEM PAGE, `/chase/<itemId>` (docs/2026-10-23-chaser-design.md §10, plan 3.6):
// which open ask the page shows, what its primary action opens, the close both the page and the Action needed
// row run, and the once-per-item "opened" signal. The same model as iOS and Android. Pure, no runtime imports —
// the React Query cache, the router and `fetch` are handed in — so it is testable without the app's graph.

/** The web path of one chase item's page. `itemId` is encoded: it crosses into a path segment. */
export function chaseItemPath(itemId: string): string {
  return `/chase/${encodeURIComponent(itemId)}`;
}

/** Where "Go to Action needed" lands: the Tasks screen on its waiting tab. */
export const ACTION_NEEDED_PATH = '/tasks?tab=waiting';

export const CHASE_ITEM_PREPARED_KEY = 'chase.item.prepared';
export const CHASE_ITEM_OPEN_CONVERSATION_KEY = 'chase.item.openConversation';
export const CHASE_ITEM_GONE_TITLE_KEY = 'chase.item.gone.title';
export const CHASE_ITEM_GONE_BODY_KEY = 'chase.item.gone.body';
export const CHASE_ITEM_GONE_ACTION_KEY = 'chase.item.gone.action';

/** The chase ask for an item id in a read, or `null`. */
export function findChaseAsk(read: OpenAsksRead | null | undefined, itemId: string): ChaseAsk | null {
  if (!read || !itemId) return null;
  const ask = read.asks.find((a) => a.chase?.itemId === itemId);
  return ask && isChaseAsk(ask) ? ask : null;
}

export type ChasePrimaryAction =
  | { kind: 'prepared'; labelKey: string; href: string; taskId: string }
  | { kind: 'conversation'; labelKey: string; href: string };

/**
 * The page's primary action: a prepared task opens its task drill-in, where the approval Decision is answered
 * in-app; otherwise the conversation the item was told in. `null` when the item carries neither — then the page
 * shows no primary button at all (a button to an empty conversation would open nothing).
 */
export function chasePrimaryAction(ask: ChaseAsk): ChasePrimaryAction | null {
  const taskId = ask.chase.preparedTaskId;
  if (taskId) return { kind: 'prepared', labelKey: CHASE_ITEM_PREPARED_KEY, href: `/tasks/${encodeURIComponent(taskId)}`, taskId };
  if (!ask.conversationId) return null;
  return { kind: 'conversation', labelKey: CHASE_ITEM_OPEN_CONVERSATION_KEY, href: `/?conversation=${encodeURIComponent(ask.conversationId)}` };
}

export type ChaseItemPageModel =
  | { kind: 'loading' }
  | { kind: 'gone' }
  | { kind: 'found'; ask: ChaseAsk; primary: ChasePrimaryAction | null };

/**
 * What the page renders. `read` is the open asks as the cache holds them now (`null` when the server omitted the
 * field); `fetched` whether a read THIS PAGE STARTED has completed — landed or failed. The cache can be up to 30s
 * stale, so an item missing from it is only GONE once the page's own read has answered (iOS
 * `ChaseItemPageModel.onOpen` is the reference); until then the page shows its loading shimmer. An item found in
 * the cache renders at once and the read refreshes it underneath. Gone is never an error: a failed read and an
 * older server that carries no open asks at all are gone too. `closing` is the item as it was when a close was
 * pressed: the close drops the row from the cache at once, and the page keeps drawing that snapshot until the
 * reply decides, instead of flashing "no longer open" before it navigates back.
 */
export function chaseItemPageModel(
  read: OpenAsksRead | null | undefined,
  fetched: boolean,
  itemId: string,
  closing: ChaseAsk | null = null,
): ChaseItemPageModel {
  if (closing) return { kind: 'found', ask: closing, primary: chasePrimaryAction(closing) };
  const ask = findChaseAsk(read, itemId);
  if (ask) return { kind: 'found', ask, primary: chasePrimaryAction(ask) };
  if (!fetched && itemId) return { kind: 'loading' };
  return { kind: 'gone' };
}

/**
 * The page heading (the AppShell title): the item's own title when it is found and has one; otherwise the neutral
 * "While you were away" section title — never "This is no longer open", which the gone card already says (and
 * which would be false while loading, or for a found item with an empty title).
 */
export function chaseItemHeading(model: ChaseItemPageModel): { text: string } | { key: string } {
  if (model.kind === 'found' && model.ask.title) return { text: model.ask.title };
  return { key: CHASE_GROUP_TITLE_KEY };
}

/**
 * Where leaving the page goes: back to where the reader came from, or — a cold landing (a link opened in a new
 * tab), which has nowhere to go back to — Action needed. Used by the desktop Back button and after a close.
 */
export function chaseItemLeaveTarget(historyLength: number): { kind: 'back' } | { kind: 'replace'; href: string } {
  return historyLength > 1 ? { kind: 'back' } : { kind: 'replace', href: ACTION_NEEDED_PATH };
}

/** The slice of the React Query cache a close touches. */
export interface ChaseCloseCache {
  updateOpenAsks(update: (current: OpenAsksRead | null | undefined) => OpenAsksRead | null | undefined): void;
  invalidateTasks(): void;
}

/** POSTs a `/api/client-signal` body; resolves with the response's `ok`. */
export type ClientSignalPost = (body: Record<string, string>) => Promise<{ ok: boolean }>;

/**
 * "I did it" / "Stop watching this" — the one close the Action needed row and the item page both run.
 * Optimistic: the row leaves the cache at once and the item is remembered as closed for this page session (so a
 * racing refresh cannot bring it back); a refused or lost close puts back ONLY that row, into the cache as it is
 * now. Resolves `true` when the server took it.
 */
export async function closeChaseItem(ask: ChaseAsk, action: 'done' | 'dismiss', cache: ChaseCloseCache, post: ClientSignalPost): Promise<boolean> {
  const { itemId } = ask.chase;
  markChaseItemClosed(itemId);
  cache.updateOpenAsks((current) => withoutChaseItem(current, itemId));
  try {
    const res = await post({ kind: action === 'done' ? 'chaseItemDone' : 'chaseItemDismissed', chaseItemId: itemId });
    if (!res.ok) throw new Error('client-signal refused the close');
  } catch {
    unmarkChaseItemClosed(itemId);
    cache.updateOpenAsks((current) => withChaseAskRestored(current, ask));
    return false;
  }
  cache.invalidateTasks();
  return true;
}

/**
 * `chaseItemOpened`, once per item: returns a reporter that sends the signal the first time it sees an item id
 * and never again for that id (a re-render, a refetch that re-finds the item). Fire-and-forget — a failure is
 * swallowed and never blocks the page. `sent` is the memory of what was reported; the page passes the
 * module-level `CHASE_OPENED_THIS_SESSION`, so returning from the conversation (which remounts the page) does not
 * report the same item again.
 */
export function chaseOpenedReporter(post: ClientSignalPost, sent: Set<string> = new Set<string>()): (itemId: string) => void {
  return (itemId) => {
    if (!itemId || sent.has(itemId)) return;
    sent.add(itemId);
    try {
      void post({ kind: 'chaseItemOpened', chaseItemId: itemId }).catch(() => {});
    } catch {
      // A synchronous throw from the transport is as silent as a rejected one.
    }
  };
}

/** The items whose page this browser tab has already reported opened — one `chaseItemOpened` per item per session. */
export const CHASE_OPENED_THIS_SESSION = new Set<string>();
